import React, { useState } from 'react';
import PropTypes from 'prop-types';
import IfClauseEditor from './IfClauseEditor';
import BranchFieldsEditor from './BranchFieldsEditor';
import RuleValidationSummary from './RuleValidationSummary';
import { summarize } from './summarize';
import { addElseBranch, hasElseBranch, removeElseBranch } from './serialization';

/**
 * Bug #7: produce a human description of a raw-only IF body whose only
 * meaningful content is a bare `required` (possibly inside `not`). Returns
 * `null` when the body has no required array — the badge isn't applicable.
 *
 * Examples:
 *   { required: ['status', 'region'] }            → "present: status, region"
 *   { not: { required: ['regulator_ref'] } }      → "not present: regulator_ref"
 */
function describeRawIf(rawIf) {
	if (!rawIf || typeof rawIf !== 'object') return null;
	const negReq = rawIf.not && Array.isArray(rawIf.not.required) ? rawIf.not.required : null;
	const posReq = Array.isArray(rawIf.required) ? rawIf.required : null;
	if (negReq && negReq.length > 0) return { label: 'not present', fields: negReq };
	if (posReq && posReq.length > 0) return { label: 'present', fields: posReq };
	return null;
}

/**
 * True when a preserved raw branch body (`rule.raw.then` / `rule.raw.else`) is a
 * plain object, i.e. parseBranch kept keywords the builder can't round-trip.
 * Arrays and `null` are not valid JSON-Schema bodies and must not badge.
 */
function isManagedBranchBody(body) {
	return !!body && typeof body === 'object' && !Array.isArray(body);
}

const MANAGED_BRANCH_NOTE =
	'Includes schema-managed constraints — edit the schema JSON to change them.';

/**
 * One rule's editor. Composes IfClauseEditor + THEN / ELSE
 * BranchFieldsEditor. Owns local collapsed state. ELSE branch is hidden
 * until the user clicks "+ Add ELSE branch".
 */
export default function RuleCard({
	rule,
	ruleIndex,
	availableFields,
	errors,
	onChange,
	onDelete,
	onDuplicate,
}) {
	const [collapsed, setCollapsed] = useState(false);
	// ELSE branch is "open" whenever the rule carries ELSE content OR the user
	// explicitly clicked `+ Add ELSE branch`. DSV-016: the click is now also
	// recorded on the rule itself (`elseBranchOpen`, via addElseBranch) so an
	// empty ELSE branch the author asked for survives remount / reorder — the
	// local flag stays only as the optimistic in-render open, mirroring the way
	// the remove handler pairs local state with the rule patch.
	const [elseOpen, setElseOpen] = useState(false);

	const patch = (partial) => onChange({ ...rule, ...partial });

	// Whenever the user removes a name from thenFields/elseFields (via the
	// branch checkbox), also drop it from the corresponding *RequiredOrder.
	// Without this, names parsed from a source `required` array (with no
	// matching `properties` entry) would re-appear on emit via
	// unionPreservingOrder, ignoring the user's uncheck.
	const patchBranchFields = (branchKey, orderKey, next) => {
		const nextNames = new Set(next.map((f) => f.fieldName));
		const partial = { [branchKey]: next };
		const order = rule[orderKey];
		if (Array.isArray(order)) {
			const filtered = order.filter((name) => nextNames.has(name));
			partial[orderKey] = filtered.length > 0 ? filtered : undefined;
		}
		patch(partial);
	};

	const hasErrors = !!errors && Object.keys(errors).length > 0;
	// hasElseBranch (not `elseFields.length`) so a rule whose ELSE lives only in
	// `elseRequiredOrder` / `raw.else` — which emitRule still serializes — shows
	// its editor instead of offering "+ Add ELSE branch" for a branch that exists.
	const showElseEditor = elseOpen || rule.elseBranchOpen === true || hasElseBranch(rule);
	// A branch may carry keywords the builder can't render (unrecognised anyOf
	// variants, oneOf, nested not). They round-trip through rule.raw, so the
	// section header flags them as read-only, schema-managed extras.
	const hasManagedThen = isManagedBranchBody(rule.raw && rule.raw.then);
	const hasManagedElse = isManagedBranchBody(rule.raw && rule.raw.else);
	// Bug #7: when parseRule encounters a bare `required` (or `not.required`)
	// IF body that has no recognised operator, it preserves the body via
	// raw.if and leaves ifConditions empty. The IF editor cannot render that
	// shape (no operator to display), so we surface a read-only badge instead.
	const rawIfDescription =
		Array.isArray(rule.ifConditions) && rule.ifConditions.length === 0
			? describeRawIf(rule.raw && rule.raw.if)
			: null;

	return (
		<div
			className={`dsv-xallof-builder__rule${hasErrors ? ' dsv-xallof-builder__rule--invalid' : ''}${collapsed ? ' dsv-xallof-builder__rule--collapsed' : ''}`}
			role="group"
			aria-label={`Conditional rule ${ruleIndex + 1}`}
			data-testid={`xallof-rule-${ruleIndex}`}
		>
			<div className="dsv-xallof-builder__rule-header">
				<button
					type="button"
					className="dsv-xallof-builder__collapse-toggle"
					onClick={() => setCollapsed(!collapsed)}
					aria-expanded={!collapsed}
					data-testid={`xallof-rule-${ruleIndex}-collapse-toggle`}
				>
					<span className="dsv-xallof-builder__chevron" aria-hidden="true">
						{collapsed ? '▸' : '▾'}
					</span>
					<span className="dsv-xallof-builder__rule-index">
						Rule {ruleIndex + 1}
					</span>
					{rule.negated && (
						<span className="dsv-xallof-builder__neg-badge">NOT</span>
					)}
					{hasErrors && (
						<span
							className="dsv-xallof-builder__error-dot"
							aria-label="Has validation errors"
							title="Has validation errors"
						/>
					)}
					{collapsed && (
						<span className="dsv-xallof-builder__rule-summary">
							{summarize(rule)}
						</span>
					)}
				</button>
				<div className="dsv-xallof-builder__rule-actions">
					{onDuplicate && (
						<button
							type="button"
							className="dsv-xallof-builder__ghost-btn"
							onClick={onDuplicate}
							title="Duplicate rule"
							data-testid={`xallof-rule-${ruleIndex}-duplicate`}
						>
							Duplicate
						</button>
					)}
					<button
						type="button"
						className="dsv-xallof-builder__ghost-btn dsv-xallof-builder__ghost-btn--danger"
						onClick={onDelete}
						title="Delete rule"
						data-testid={`xallof-rule-${ruleIndex}-delete`}
					>
						Delete
					</button>
				</div>
			</div>

			{!collapsed && (
				<div className="dsv-xallof-builder__rule-body">
					{rawIfDescription ? (
						<section
							className="dsv-xallof-builder__section dsv-xallof-builder__section--if dsv-xallof-builder__section--readonly"
							aria-label="IF clause (schema-managed)"
							data-testid={`xallof-rule-${ruleIndex}-raw-if-badge`}
						>
							<header className="dsv-xallof-builder__section-head">
								<span className="dsv-xallof-builder__section-label dsv-xallof-builder__section-label--if">
									IF
								</span>
								<span className="dsv-xallof-builder__section-meta">
									Schema-managed — edit the schema JSON to change this IF body.
								</span>
							</header>
							<p className="dsv-xallof-builder__raw-if-summary">
								{rawIfDescription.label}: <code>{rawIfDescription.fields.join(', ')}</code>
							</p>
						</section>
					) : (
						<IfClauseEditor
							ifConditions={rule.ifConditions}
							negated={rule.negated}
							availableFields={availableFields}
							ruleIndex={ruleIndex}
							errors={errors?.ifConditions}
							onChange={(next) => patch({ ifConditions: next })}
							onNegatedChange={(n) => patch({ negated: n })}
						/>
					)}

					<section
						className="dsv-xallof-builder__section dsv-xallof-builder__section--then"
						aria-label="THEN branch"
					>
						<header className="dsv-xallof-builder__section-head">
							<span className="dsv-xallof-builder__section-label dsv-xallof-builder__section-label--then">
								THEN
							</span>
							<span className="dsv-xallof-builder__section-meta">
								apply these constraints when the IF matches
							</span>
							{hasManagedThen && (
								<span
									className="dsv-xallof-builder__section-meta dsv-xallof-builder__section-meta--managed"
									data-testid={`xallof-rule-${ruleIndex}-raw-then-badge`}
								>
									{MANAGED_BRANCH_NOTE}
								</span>
							)}
						</header>
						<BranchFieldsEditor
							branch="then"
							ruleIndex={ruleIndex}
							selectedFields={rule.thenFields}
							availableFields={availableFields}
							errors={errors?.thenFields}
							onChange={(next) => patchBranchFields('thenFields', 'thenRequiredOrder', next)}
						/>
					</section>

					<section
						className="dsv-xallof-builder__section dsv-xallof-builder__section--else"
						aria-label="ELSE branch"
					>
						<header className="dsv-xallof-builder__section-head">
							<span className="dsv-xallof-builder__section-label dsv-xallof-builder__section-label--else">
								ELSE
							</span>
							<span className="dsv-xallof-builder__section-meta">
								{showElseEditor
									? 'constraints applied when the IF does not match'
									: 'optional — constraints when the IF does not match'}
							</span>
							{hasManagedElse && (
								<span
									className="dsv-xallof-builder__section-meta dsv-xallof-builder__section-meta--managed"
									data-testid={`xallof-rule-${ruleIndex}-raw-else-badge`}
								>
									{MANAGED_BRANCH_NOTE}
								</span>
							)}
							{showElseEditor && (
								<button
									type="button"
									className="dsv-xallof-builder__ghost-btn dsv-xallof-builder__ghost-btn--sm"
									onClick={() => {
										setElseOpen(false);
										// DSV-015: clear the entire ELSE state (fields +
										// required order + raw.else), not just elseFields.
										onChange(removeElseBranch(rule));
									}}
									title="Remove ELSE branch"
									data-testid={`xallof-rule-${ruleIndex}-remove-else-branch`}
								>
									Remove branch
								</button>
							)}
						</header>
						{showElseEditor ? (
							<BranchFieldsEditor
								branch="else"
								ruleIndex={ruleIndex}
								selectedFields={rule.elseFields}
								availableFields={availableFields}
								errors={errors?.elseFields}
								onChange={(next) => patchBranchFields('elseFields', 'elseRequiredOrder', next)}
							/>
						) : (
							<button
								type="button"
								className="dsv-xallof-builder__add-cond"
								onClick={() => {
									setElseOpen(true);
									// DSV-016: record the branch on the rule too (mirror of
									// the Remove branch handler). Branch-scoped "Accept
									// null" widens the branch it is authored on, so a
									// relaxing constraint needs a real ELSE to live in —
									// on the THEN side it lands where the field is
									// mandatory and weakens it to presence-only.
									onChange(addElseBranch(rule));
								}}
								data-testid={`xallof-rule-${ruleIndex}-add-else-branch`}
							>
								+ Add ELSE branch
							</button>
						)}
					</section>

					<RuleValidationSummary errors={errors} ruleIndex={ruleIndex} />
				</div>
			)}
		</div>
	);
}

RuleCard.propTypes = {
	rule: PropTypes.object.isRequired,
	ruleIndex: PropTypes.number.isRequired,
	availableFields: PropTypes.array.isRequired,
	errors: PropTypes.object,
	onChange: PropTypes.func.isRequired,
	onDelete: PropTypes.func.isRequired,
	onDuplicate: PropTypes.func,
};
